import { Component, Input, OnInit, OnDestroy, Output, EventEmitter, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { UnsavedReportModalComponent } from '../unsave-report-modal/unsave-report-modal.component';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { ReportsService } from '@app-reports/services/reports/reports.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DataService } from '@app-core/services/data/data.service';

@Component({
  selector: 'app-save-report-modal',
  templateUrl: './save-report-modal.component.html',
  styleUrls: ['./save-report-modal.component.scss'],
})
export class SaveReportModalComponent implements OnInit, OnDestroy {
  @Output() modalClose = new EventEmitter<void>();
  @Input() question: string;
  @Input() answer: string;
  @Input() dateRange: string[];
  @Input() tagIds: number[];
  @Input() filterSelectedTagIds: number[];
  @Input() sessionId: string;
  @Input() reportName: string;
  @Input() reportDescription: string;
  @Input() fleetIds: string;
  @Input() sql: string | string[];
  @Input() executedSql: string[];
  @Input() metadata: Record<string, any>;
  @Input() reportSourceId: number | null = null;
  @Input() getCustomReportsListData: () => void;
  @Input() isEditMode: boolean = false;
  @Input() customReportId: string = '';

  private ngUnsubscribe: Subject<void> = new Subject<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  saveReportForm: FormGroup;
  maxDescriptionLength = 150;
  isSaving = false;
  private copyCounter = 0;
  private baseReportName = '';

  constructor(
    private fb: FormBuilder,
    private snackbarService: SnackBarService,
    private dialog: MatDialog,
    private reportService: ReportsService,
    private translate: TranslateService,
    public dataService: DataService,
    @Inject(MAT_DIALOG_DATA) public data: SaveReportModalComponent
  ) {}

  ngOnInit() {
    this.saveReportForm = this.fb.group({
      reportName: [this.reportName || '', [Validators.required, Validators.maxLength(100), this.noWhitespaceValidator]],
      reportDescription: [this.reportDescription || '', [Validators.maxLength(this.maxDescriptionLength)]],
    });
  }

  noWhitespaceValidator(control: AbstractControl): ValidationErrors | null {
    const isWhitespace = (control.value || '').trim().length === 0;
    const isValid = !isWhitespace;
    return isValid ? null : { required: true };
  }

  get reportNameControl() {
    return this.saveReportForm.get('reportName');
  }

  get reportDescriptionControl() {
    return this.saveReportForm.get('reportDescription');
  }

  get descriptionLength() {
    return this.reportDescriptionControl?.value?.length || 0;
  }

  onCancelModal() {
    this.modalClose.emit();
  }

  onCancel() {
    this.dialog.open(UnsavedReportModalComponent, {
      height: '240px',
      maxWidth: '400px',
      width: '100%',
      disableClose: true,
      autoFocus: false,
      panelClass: ['custom-report-modal'],
      data: {
        onCancelModal: () => this.onCancelModal(),
        onSaveReport: () => this.onSave(),
      },
    });
  }

  private getMergedTagIds(): number[] {
    const tagIds = this.tagIds || [];
    const filterTagIds = this.filterSelectedTagIds || [];
    const mergedTags = [...new Set([...tagIds, ...filterTagIds])];
    return mergedTags;
  }

  private generateCopyName(baseName: string, counter: number): string {
    const cleanName = baseName.replace(/\s+copy-\d+$/, '');
    return `${cleanName} copy-${counter}`;
  }

  private editReport(reportData: any, fleetId: string): void {
    this.isSaving = true;
    this.reportService
      .editCustomReport(this.customReportId, reportData)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: () => {
          this.isSaving = false;
          this.snackbarService.success(this.translate.instant('Report updated successfully'));
          if (this.getCustomReportsListData) {
            this.getCustomReportsListData();
          }
          this.data?.getCustomReportsListData?.();
          this.onCancelModal();
          this.saveSuccess.emit();
        },
        error: (err) => {
          this.isSaving = false;
          if (err?.status === 409) {
            this.copyCounter++;
            const newReportName = this.generateCopyName(this.baseReportName, this.copyCounter);
            this.reportNameControl?.setValue(newReportName);
            this.editReport({ ...reportData, reportName: newReportName }, fleetId);
          } else if (err?.status === 406) {
            this.snackbarService.failure(this.translate.instant('Report limit reached: Only 20 reports are allowed per fleet.'));
          } else {
            this.snackbarService.failure(this.translate.instant('Failed to update report. Please try again.'));
          }
        },
      });
  }

  private saveReport(reportData: any, fleetId: string): void {
    this.isSaving = true;
    this.reportService
      .saveCustomReport(fleetId, reportData)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: () => {
          this.isSaving = false;
          this.snackbarService.success(this.translate.instant('Report saved successfully'));
          if (this.getCustomReportsListData) {
            this.getCustomReportsListData();
          }
          this.data?.getCustomReportsListData?.();
          this.onCancelModal();
          this.saveSuccess.emit();
        },
        error: (err) => {
          this.isSaving = false;
          if (err?.status === 409) {
            this.copyCounter++;
            const newReportName = this.generateCopyName(this.baseReportName, this.copyCounter);
            this.reportNameControl?.setValue(newReportName);
            this.saveReport({ ...reportData, reportName: newReportName }, fleetId);
          } else if (err?.status === 406) {
            this.snackbarService.failure(this.translate.instant('Report limit reached: Only 20 reports are allowed per fleet.'));
          } else {
            this.snackbarService.failure(this.translate.instant('tripListComponentTripsDownloadFailure'));
          }
        },
      });
  }

  onSave(): void {
    if (this.saveReportForm.invalid) {
      this.saveReportForm.markAllAsTouched();
      return;
    }

    const trimmedReportName = this.reportNameControl?.value?.trim();
    const mergedTagIds = this.getMergedTagIds();

    this.baseReportName = trimmedReportName.replace(/\s+copy-\d+$/, '');
    this.copyCounter = 0;

    const hasSourceId = Number.isInteger(this.reportSourceId);
    const metadataToSend =
      this.isEditMode && hasSourceId ? { ...(this.metadata || {}), reportSourceId: this.reportSourceId } : this.metadata;

    const tqi: number = (this.metadata as any)?.tableQueryIndex ?? 0;

    const reportData = {
      reportName: trimmedReportName,
      reportDescription: this.reportDescriptionControl?.value?.trim() || '',
      reportQuery: this.executedSql?.[tqi] ?? this.executedSql?.[0] ?? '',
      fleetId: this.fleetIds,
      ...(hasSourceId ? { reportSourceId: this.reportSourceId } : {}),
      reportPayload: {
        sessionId: this.sessionId,
        question: this.question,
        answer: this.answer,
        tagIds: mergedTagIds,
        dateRange: this.dateRange,
        sql: this.sql,
      },
      ...(metadataToSend && Object.keys(metadataToSend || {}).length > 0 && { metadata: metadataToSend }),
    };

    const fleetId = this.fleetIds;
    if (this.isEditMode && this.customReportId) {
      this.editReport(reportData, fleetId);
    } else {
      this.saveReport(reportData, fleetId);
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
